import React, { useReducer } from 'react';
import { X, ChevronUp, ChevronDown } from 'lucide-react';

// The two transient text inputs (keyword + exclude) are a single editing
// concern, so they share one reducer instead of two separate useState calls.
const initialInputs = { keywordInput: '', excludeInput: '' };

function inputsReducer(state, action) {
    switch (action.type) {
        case 'SET_KEYWORD':
            return { ...state, keywordInput: action.value };
        case 'SET_EXCLUDE':
            return { ...state, excludeInput: action.value };
        case 'CLEAR_KEYWORD':
            return { ...state, keywordInput: '' };
        case 'CLEAR_EXCLUDE':
            return { ...state, excludeInput: '' };
        default:
            return state;
    }
}

export default function KeywordsSection({
    filters,
    onChange,
    isExpanded,
    onToggleExpanded,
    showExclude,
    onToggleExclude,
    onClear,
}) {
    const [inputs, dispatch] = useReducer(inputsReducer, initialInputs);
    const { keywordInput, excludeInput } = inputs;

    const searchIn = filters.searchIn || {};
    const isActive = (
        filters.keywords.length > 0 ||
        filters.excludeKeywords.length > 0 ||
        filters.highlight === false ||
        searchIn.title === false || searchIn.description === false || searchIn.skills === false
    );

    const handleAddKeyword = (e) => {
        if (e.key === 'Enter' && keywordInput.trim()) {
            onChange({
                ...filters,
                keywords: [...filters.keywords, keywordInput.trim()]
            });
            dispatch({ type: 'CLEAR_KEYWORD' });
        }
    };

    const handleRemoveKeyword = (index) => {
        const newKeywords = filters.keywords.filter((_, i) => i !== index);
        onChange({ ...filters, keywords: newKeywords });
    };

    const handleAddExclude = (e) => {
        if (e.key === 'Enter' && excludeInput.trim()) {
            onChange({
                ...filters,
                excludeKeywords: [...filters.excludeKeywords, excludeInput.trim()]
            });
            dispatch({ type: 'CLEAR_EXCLUDE' });
        }
    };

    const handleRemoveExclude = (index) => {
        const newExcludes = filters.excludeKeywords.filter((_, i) => i !== index);
        onChange({ ...filters, excludeKeywords: newExcludes });
    };

    const toggleSearchIn = (field) => {
        onChange({
            ...filters,
            searchIn: {
                ...filters.searchIn,
                [field]: !filters.searchIn[field]
            }
        });
    };

    return (
        <div className="bg-white p-4 rounded-xl border border-gray-100 h-fit">
            <button
                type="button"
                className={`w-full flex items-center justify-between cursor-pointer ${isExpanded ? 'mb-4' : ''}`}
                onClick={onToggleExpanded}
            >
                <div className="flex items-center gap-2 font-semibold text-gray-700">
                    <span className="text-lg">#</span>
                    <span>Keywords</span>
                </div>
                <div className="flex items-center gap-2">
                    {filters.keywords.length > 0 && (
                        <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">
                            {filters.keywords.length}
                        </span>
                    )}
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
            </button>

            {isExpanded && (
                <div className="space-y-4">
                    {isActive && (
                        <div className="flex justify-end -mt-2">
                            <button
                                type="button"
                                onClick={onClear}
                                className="text-xs font-medium text-gray-400 hover:text-red-600 focus:outline-none transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    )}
                    {/* Search Keywords Input */}
                    <div>
                        <label htmlFor="jf-keyword-input" className="block text-sm font-medium text-gray-700 mb-1">
                            Search keywords or phrases:
                        </label>
                        <div className="relative">
                            <input
                                id="jf-keyword-input"
                                type="text"
                                value={keywordInput}
                                onChange={(e) => dispatch({ type: 'SET_KEYWORD', value: e.target.value })}
                                onKeyDown={handleAddKeyword}
                                placeholder="Enter keyword and press Enter"
                                className="w-full pl-3 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-blue-300 transition-colors"
                            />
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                                <div className="w-5 h-5 bg-teal-600 rounded-full flex items-center justify-center text-white text-xs">
                                    G
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Keyword Tags */}
                    {filters.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {filters.keywords.map((kw, idx) => (
                                <span key={kw} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm flex items-center gap-1 group">
                                    {kw}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveKeyword(idx)}
                                        className="hover:text-blue-900 focus:outline-none"
                                        aria-label={`Remove keyword ${kw}`}
                                    >
                                        <X size={14} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Exclude Toggle */}
                    <div className="flex items-center gap-3 pt-2">
                        <button
                            type="button"
                            aria-label="Toggle exclude keywords"
                            onClick={onToggleExclude}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${showExclude ? 'bg-blue-600' : 'bg-gray-200'}`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showExclude ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                        <span className="text-sm text-gray-600">Set exclude keywords</span>
                    </div>

                    {/* Exclude Keywords Input */}
                    {showExclude && (
                        <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                            <label htmlFor="jf-exclude-input" className="block text-sm font-medium text-gray-700 mb-1">
                                Exclude keywords:
                            </label>
                            <input
                                id="jf-exclude-input"
                                type="text"
                                value={excludeInput}
                                onChange={(e) => dispatch({ type: 'SET_EXCLUDE', value: e.target.value })}
                                onKeyDown={handleAddExclude}
                                placeholder="Enter keyword and press Enter"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 hover:border-red-300 transition-colors"
                            />

                            {/* Exclude Tags */}
                            {filters.excludeKeywords.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {filters.excludeKeywords.map((kw, idx) => (
                                        <span key={kw} className="bg-red-50 text-red-700 px-2 py-1 rounded text-sm flex items-center gap-1">
                                            {kw}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveExclude(idx)}
                                                className="hover:text-red-900 focus:outline-none"
                                                aria-label={`Remove exclude keyword ${kw}`}
                                            >
                                                <X size={14} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}



                    {/* Advanced Options */}
                    <div className="pt-2">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Advanced Keywords Options:</h4>
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filters.searchIn.title}
                                    onChange={() => toggleSearchIn('title')}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">Search in title</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filters.searchIn.description}
                                    onChange={() => toggleSearchIn('description')}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">Search in description</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filters.searchIn.skills}
                                    onChange={() => toggleSearchIn('skills')}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">Search in skills</span>
                            </label>
                        </div>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer pt-4">
                        <input
                            type="checkbox"
                            checked={filters.highlight || false}
                            onChange={(e) => onChange({ ...filters, highlight: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Highlight keywords</span>
                    </label>

                </div>
            )}
        </div>
    );
}
